// focus.js
// Conversational focus tracking so voice commands like "add it", "fix that",
// or "why did I get a B on HIS note" resolve to the right card/patient.
// Resolution order: explicit name match > last card interacted with >
// last patient mentioned > single-candidate fallback.
export class FocusTracker {
  constructor() {
    this.lastQueueItemId = null;
    this.lastPatientName = null;
  }

  touchQueueItem(item) {
    if (!item) return;
    this.lastQueueItemId = item.id;
    if (item.patientName) this.lastPatientName = item.patientName;
  }

  touchPatient(patientName) {
    if (patientName) this.lastPatientName = patientName;
  }

  /**
   * Resolve a queue item from optional hints.
   * @param {LiveStore} store
   * @param {{itemId?: string, patientName?: string, kind?: string}} hints
   */
  resolveQueueItem(store, hints = {}) {
    const queue = store
      .getState()
      .queue.filter((q) => q.status === 'pending' || q.status === 'working');

    if (hints.itemId) {
      const byId = queue.find((q) => q.id === hints.itemId);
      if (byId) return byId;
    }

    const name = (hints.patientName || '').trim().toLowerCase();
    if (name) {
      const byName = queue.filter((q) =>
        (q.patientName || '').toLowerCase().includes(name),
      );
      const pool = hints.kind
        ? byName.filter((q) => q.kind === hints.kind)
        : byName;
      if (pool.length) return pool[0];
    }

    if (this.lastQueueItemId) {
      const byFocus = queue.find((q) => q.id === this.lastQueueItemId);
      if (byFocus && (!hints.kind || byFocus.kind === hints.kind))
        return byFocus;
    }

    if (this.lastPatientName) {
      const byLastPatient = queue.find(
        (q) =>
          (q.patientName || '').toLowerCase() ===
            this.lastPatientName.toLowerCase() &&
          (!hints.kind || q.kind === hints.kind),
      );
      if (byLastPatient) return byLastPatient;
    }

    const pool = hints.kind
      ? queue.filter((q) => q.kind === hints.kind)
      : queue;
    return pool.length === 1 ? pool[0] : null;
  }

  resolvePatient(store, patientName) {
    const sessions = store.getState().sessions;
    const name = (patientName || this.lastPatientName || '')
      .trim()
      .toLowerCase();
    if (!name) return null;
    return (
      sessions.find((s) =>
        (s.patientName || '').toLowerCase().includes(name),
      ) || null
    );
  }

  /**
   * Cold-open resolution: today's sessions first, then the whole patient
   * panel (apiClient.patients, cached on profile load). Ambient principle:
   * every tool works with no visit open. Returns a session-shaped object;
   * panel-only matches have conversationId: null.
   */
  resolvePatientGlobal(store, apiClient, patientName) {
    const fromSessions = this.resolvePatient(store, patientName);
    if (fromSessions) return fromSessions;

    const name = (patientName || this.lastPatientName || '')
      .trim()
      .toLowerCase();
    if (!name) return null;
    const patient = (apiClient?.patients || []).find((p) =>
      `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(name),
    );
    if (!patient) return null;
    return {
      conversationId: null,
      patientName:
        `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
      group: patient.uid,
      status: null,
      recording: false,
      grade: '--',
    };
  }
}

export const focusTracker = new FocusTracker();
export default focusTracker;
